/**
 * Store Card Component
 *
 * Premium fintech-style card displaying store health signals at a glance.
 * Features glowing borders based on open/closed status.
 *
 * @module StoreCommandCenter/components
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  LocalShipping as TransitIcon,
  Inventory2 as PackingIcon,
  HourglassEmpty as WaitingIcon,
  AccessTime as TimeIcon,
  FiberManualRecord as StatusDot,
  Settings as CustomIcon,
  Phone as PhoneIcon,
  ContentCut as CutoffIcon,
  PlaylistAddCheck as PickingIcon,
  Schedule as ComingSoonIcon,
  CheckCircleOutline as ReadyIcon,
} from '@mui/icons-material';
import type { StoreCommandData, HealthSignals } from '../hooks/useCommandCenter';
import { formatMinutes, getTotalActiveOrders } from '../hooks/useCommandCenter';
import { useAdminTheme, type AdminColorTokens } from '../../../admin-theme';

// ============================================================================
// Types
// ============================================================================

interface StoreCardProps {
  store: StoreCommandData;
  onClick: (storeId: string) => void;
  isSelected?: boolean;
}

// ============================================================================
// Styles
// ============================================================================

/**
 * Get glow effect styles based on store status (mode-aware)
 */
const getGlowStyles = (isOpen: boolean, isActive: boolean, colors: AdminColorTokens, isPaused = false) => {
  // Inactive stores: amber glow ("coming soon"). Paused ones share it — both
  // are "not taking orders", one by the admin, one by the staff.
  if (!isActive || isPaused) {
    return {
      boxShadow: `0 0 15px ${alpha(colors.warning, 0.2)}, inset 0 0 15px ${alpha(colors.warning, 0.02)}`,
      border: `2px solid ${alpha(colors.warning, 0.35)}`,
      opacity: 0.8,
      '&:hover': {
        boxShadow: `0 0 25px ${alpha(colors.warning, 0.3)}, inset 0 0 20px ${alpha(colors.warning, 0.04)}`,
        opacity: 0.95,
      },
    };
  }

  if (isOpen) {
    return {
      boxShadow: `0 0 20px ${alpha(colors.success, 0.25)}, inset 0 0 20px ${alpha(colors.success, 0.03)}`,
      border: `2px solid ${alpha(colors.success, 0.4)}`,
      '&:hover': {
        boxShadow: `0 0 30px ${alpha(colors.success, 0.35)}, inset 0 0 30px ${alpha(colors.success, 0.05)}`,
      },
    };
  }

  return {
    boxShadow: `0 0 15px ${alpha(colors.error, 0.15)}, inset 0 0 15px ${alpha(colors.error, 0.02)}`,
    border: `2px solid ${alpha(colors.error, 0.25)}`,
    opacity: 0.85,
    '&:hover': {
      boxShadow: `0 0 20px ${alpha(colors.error, 0.25)}, inset 0 0 20px ${alpha(colors.error, 0.03)}`,
      opacity: 1,
    },
  };
};

// ============================================================================
// Sub-components
// ============================================================================

interface SignalBadgeProps {
  icon: React.ReactElement;
  count: number;
  label: string;
  color: 'info' | 'warning' | 'success' | 'error';
  themeColors: AdminColorTokens;
  mode: 'light' | 'dark';
}

/**
 * Health signal badge with icon and count
 */
function SignalBadge({ icon, count, label, color, themeColors, mode }: SignalBadgeProps) {
  const colorMap = {
    info: themeColors.info,
    warning: themeColors.warning,
    success: themeColors.successGlow,
    error: themeColors.errorGlow,
  };

  const badgeColor = colorMap[color];
  const isActive = count > 0;
  const subtleBg = mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)';
  const subtleBorder = mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const subtleHover = mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';

  return (
    <Tooltip title={`${label}: ${count}`}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          bgcolor: isActive ? alpha(badgeColor, 0.08) : subtleBg,
          border: `1px solid ${isActive ? alpha(badgeColor, 0.25) : subtleBorder}`,
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: isActive ? alpha(badgeColor, 0.12) : subtleHover,
          },
        }}
      >
        {React.cloneElement(icon, {
          sx: { fontSize: 18, color: isActive ? badgeColor : themeColors.textMuted },
        } as any)}
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: isActive ? badgeColor : themeColors.textMuted }}
        >
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format time string (HH:MM:SS or HH:MM) to display format (HH:MM)
 */
function formatTime(time: string): string {
  return time.split(':').slice(0, 2).join(':');
}

/**
 * Clean store name by removing common prefixes
 */
function cleanStoreName(name: string): string {
  // Remove "DEELIVER - " prefix if present
  return name.replace(/^DEELIVER\s*-\s*/i, '');
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Store Card with health signals and glow effects
 */

export function StoreCard({ store, onClick, isSelected = false }: StoreCardProps) {
  const { mode, colors } = useAdminTheme();
  const totalActive = getTotalActiveOrders(store.signals || { inTransit: 0, packing: 0, awaitingPickup: 0 });
  const isActive = store.isActive !== false;
  // Paused outranks open/closed: the staff said "not now".
  const statusColor = store.isPaused ? colors.warning : store.isOpen ? colors.successGlow : colors.errorGlow;

  // Check if store is using default hours (no workingHours means using defaults)
  // This is a simplified check - the backend should ideally tell us this
  const isUsingDefaults = !store.workingHours;

  // Clean store name for display
  const displayName = cleanStoreName(store.name);

  const handleCardClick = () => {
    onClick(store.id);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        height: '100%',
        bgcolor: colors.bgCard,
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
        cursor: 'pointer',
        ...getGlowStyles(store.isOpen, isActive, colors, store.isPaused),
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
        }}
      >
        <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header: Name and Status */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  color: colors.textPrimary,
                  letterSpacing: '-0.02em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '1rem',
                }}
              >
                {displayName}
              </Typography>
              {store.address && (
                <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block' }}>
                  {[store.address.suburb, store.address.city].filter(Boolean).join(', ')}
                </Typography>
              )}
              {store.phoneNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <PhoneIcon sx={{ fontSize: 12, color: colors.textMuted }} />
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                    {store.phoneNumber}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Status Chips */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              {/* Open / Paused / Closed */}
              <Chip
                icon={
                  <StatusDot
                    sx={{
                      fontSize: 10,
                      color: statusColor,
                    }}
                  />
                }
                label={store.isPaused ? 'PAUSED' : store.isOpen ? 'OPEN' : 'CLOSED'}
                size="small"
                sx={{
                  bgcolor: alpha(statusColor, 0.15),
                  color: statusColor,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '0.05em',
                  border: `1px solid ${alpha(statusColor, 0.3)}`,
                  '& .MuiChip-icon': {
                    ml: 0.5,
                  },
                }}
              />

              {/* Coming Soon Badge - inactive store */}
              {!isActive && (
                <Chip
                  icon={<ComingSoonIcon sx={{ fontSize: 12 }} />}
                  label="COMING SOON"
                  size="small"
                  sx={{
                    bgcolor: alpha(colors.warning, 0.15),
                    color: colors.warning,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    letterSpacing: '0.05em',
                    border: `1px solid ${alpha(colors.warning, 0.3)}`,
                    height: 22,
                    '& .MuiChip-icon': {
                      ml: 0.5,
                      color: colors.warning,
                    },
                  }}
                />
              )}

              {/* Ready to Activate Badge - inactive store with menu data synced */}
              {!isActive && store.lastMenuSync && (
                <Tooltip title={`Menu synced ${new Date(store.lastMenuSync).toLocaleDateString()} — ready to activate`}>
                  <Chip
                    icon={<ReadyIcon sx={{ fontSize: 12 }} />}
                    label="READY"
                    size="small"
                    sx={{
                      bgcolor: alpha(colors.successGlow, 0.15),
                      color: colors.successGlow,
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                      border: `1px solid ${alpha(colors.successGlow, 0.3)}`,
                      height: 22,
                      animation: 'readyPulse 2s ease-in-out infinite',
                      '@keyframes readyPulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                        '100%': { opacity: 1 },
                      },
                      '& .MuiChip-icon': {
                        ml: 0.5,
                        color: colors.successGlow,
                      },
                    }}
                  />
                </Tooltip>
              )}

              {/* Cutoff Badge - show if past cutoff and store is open */}
              {store.isPastCutoff && store.isOpen && (
                <Tooltip title={`Past cutoff time (${store.earlyCutoff ? formatTime(store.earlyCutoff) : 'N/A'})`}>
                  <Chip
                    icon={<CutoffIcon sx={{ fontSize: 12 }} />}
                    label="CUTOFF"
                    size="small"
                    sx={{
                      bgcolor: alpha(colors.warning, 0.15),
                      color: colors.warning,
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                      border: `1px solid ${alpha(colors.warning, 0.3)}`,
                      height: 22,
                      '& .MuiChip-icon': {
                        ml: 0.5,
                        color: colors.warning,
                      },
                    }}
                  />
                </Tooltip>
              )}

              {/* Picking Flow Badge - only for Diep River HQ */}
              {store.enablePickingFlow && (
                <Tooltip title="Picking flow enabled (HQ)">
                  <Chip
                    icon={<PickingIcon sx={{ fontSize: 12 }} />}
                    label="HQ"
                    size="small"
                    sx={{
                      bgcolor: alpha(colors.info, 0.15),
                      color: colors.info,
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                      border: `1px solid ${alpha(colors.info, 0.3)}`,
                      height: 22,
                      '& .MuiChip-icon': {
                        ml: 0.5,
                        color: colors.info,
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Working Hours & Cutoff */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeIcon sx={{ fontSize: 16, color: colors.textMuted }} />
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {store.workingHours
                  ? `Today: ${formatTime(store.workingHours.open)} - ${formatTime(store.workingHours.close)}`
                  : 'Using default hours'}
              </Typography>
              {isUsingDefaults && (
                <Tooltip title="Using system default working hours">
                  <CustomIcon sx={{ fontSize: 14, color: colors.info, ml: 0.5 }} />
                </Tooltip>
              )}
            </Box>
            {store.earlyCutoff && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CutoffIcon sx={{ fontSize: 14, color: store.isPastCutoff ? colors.warning : colors.textMuted }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: store.isPastCutoff ? colors.warning : colors.textMuted,
                    fontWeight: store.isPastCutoff ? 600 : 400,
                  }}
                >
                  Cutoff: {formatTime(store.earlyCutoff)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Divider */}
          <Box
            sx={{
              height: 1,
              bgcolor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
              mx: -2.5,
              mb: 2,
            }}
          />

          {/* Health Signals Section */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textSecondary,
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
                display: 'block',
                mb: 1,
              }}
            >
              HEALTH SIGNALS
            </Typography>

            {/* Health Signal Badges */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <SignalBadge
                icon={<TransitIcon />}
                count={store.signals?.inTransit ?? 0}
                label="In Transit"
                color="info"
                themeColors={colors}
                mode={mode}
              />
              <SignalBadge
                icon={<PackingIcon />}
                count={store.signals?.packing ?? 0}
                label="Packing"
                color="warning"
                themeColors={colors}
                mode={mode}
              />
              <SignalBadge
                icon={<WaitingIcon />}
                count={store.signals?.awaitingPickup ?? 0}
                label="Awaiting Pickup"
                color="success"
                themeColors={colors}
                mode={mode}
              />
            </Box>
          </Box>

          {/* Performance Metrics */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              pt: 1,
              borderTop: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
            }}
          >
            {/* Avg Fulfillment Time */}
            <Box>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', fontSize: '0.65rem' }}>
                Avg Fulfillment
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{
                  color: store.performance?.avgFulfillmentMinutes ? colors.info : colors.textMuted,
                }}
              >
                {formatMinutes(store.performance?.avgFulfillmentMinutes ?? null)}
              </Typography>
            </Box>

            {/* Orders Completed */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', fontSize: '0.65rem' }}>
                24h Orders
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{
                  color: (store.performance?.ordersCompleted ?? 0) > 0 ? colors.textPrimary : colors.textMuted,
                }}
              >
                {store.performance?.ordersCompleted ?? 0}
              </Typography>
            </Box>

            {/* Total Active */}
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', fontSize: '0.65rem' }}>
                Active Now
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{
                  color: totalActive > 10 ? colors.warning : totalActive > 0 ? colors.successGlow : colors.textMuted,
                  bgcolor: totalActive > 0 ? alpha(colors.success, 0.1) : 'transparent',
                  px: totalActive > 0 ? 1 : 0,
                  borderRadius: 1,
                }}
              >
                {totalActive}
              </Typography>
            </Box>
          </Box>

        </CardContent>
      </Box>
    </Card>
  );
}

export default StoreCard;
